import React from 'react';
import { AlertCircle, Loader2, Paperclip, Upload } from 'lucide-react';
import { Badge, Button, Input } from '@roger/ui-components';
import type {
  TaskListResponse,
  WoundAssessmentResponse,
  WoundAttachmentResponse,
} from '../../services/api/types.gen';
import { formatDateToMMDDYYYY } from '../../utils/dateUtils';
import WoundAttachmentRow from './WoundAttachmentRow';

const formatAssessmentDate = (dateStr: string): string => {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatSize = (a: WoundAssessmentResponse): string => {
  if (a.size_na) return 'N/A';
  const parts = [a.length_cm, a.width_cm, a.depth_cm].map(n =>
    n != null ? `${n}` : '–'
  );
  if (parts.every(p => p === '–')) return 'Not measured';
  return `${parts[0]} × ${parts[1]} × ${parts[2]} cm`;
};

const formatVisitType = (visitType: string): string =>
  visitType
    .replace(/_/g, ' ')
    .replace(/\b(\w)(\w*)/g, (_match, first, rest) => {
      const word = first + rest;
      if (/^[A-Z][A-Z0-9]*$/.test(word) && word.length <= 5) return word;
      return first.toUpperCase() + rest.toLowerCase();
    });

interface WoundAssessmentCardProps {
  assessment: WoundAssessmentResponse;
  attachments: WoundAttachmentResponse[];
  readOnly: boolean;
  visits: TaskListResponse[];
  isUploadingAttachment: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onUploadAttachment: (file: File) => void;
  onDownloadAttachment: (attachment: WoundAttachmentResponse) => void;
  onDeleteAttachment: (attachmentId: string) => Promise<void>;
}

const WoundAssessmentCard: React.FC<WoundAssessmentCardProps> = ({
  assessment,
  attachments,
  readOnly,
  visits,
  isUploadingAttachment,
  onEdit,
  onDelete,
  onUploadAttachment,
  onDownloadAttachment,
  onDeleteAttachment,
}) => {
  const inputId = `upload-attachment-${assessment.id}`;

  const visitLabel = (() => {
    const v = visits.find(v => v.id === assessment.visit_id);
    if (!v) return formatAssessmentDate(assessment.created_at);
    return `${v.display_name ?? formatVisitType(v.task_type)} — ${formatDateToMMDDYYYY(v.task_date ?? v.scheduled_date)}`;
  })();

  return (
    <div className='border rounded-md p-3 space-y-2 bg-card'>
      <div className='flex items-start justify-between gap-2'>
        <div className='space-y-1 flex-1 min-w-0'>
          <div className='flex items-center gap-2 flex-wrap'>
            <span className='text-sm font-medium'>{visitLabel}</span>
            {!assessment.is_addressed && (
              <Badge variant='destructive' className='text-xs'>
                <AlertCircle className='h-3 w-3 mr-1' />
                Not addressed
              </Badge>
            )}
          </div>
          <p className='text-xs text-muted-foreground'>
            Size: {formatSize(assessment)}
          </p>
          <div className='flex gap-2 flex-wrap'>
            {assessment.drainage && assessment.drainage !== 'not_assessed' && (
              <Badge variant='outline' className='text-xs'>
                Drainage: {assessment.drainage}
              </Badge>
            )}
            {assessment.odor && assessment.odor !== 'not_assessed' && (
              <Badge variant='outline' className='text-xs'>
                Odor: {assessment.odor}
              </Badge>
            )}
          </div>
        </div>
        {!readOnly && (
          <div className='flex gap-1 shrink-0'>
            <Button variant='outline' size='sm' onClick={onEdit}>
              Edit
            </Button>
            <Button variant='outline' size='sm' onClick={onDelete}>
              Delete
            </Button>
          </div>
        )}
      </div>

      {/* Per-assessment attachments */}
      <div className='pt-1 border-t space-y-1.5'>
        <div className='flex items-center justify-between'>
          <span className='text-[11px] font-medium uppercase tracking-wide text-muted-foreground flex items-center gap-1'>
            <Paperclip className='h-3 w-3' />
            Attachments
            {attachments.length > 0 && (
              <span className='ml-0.5'>({attachments.length})</span>
            )}
          </span>
          {!readOnly && (
            <>
              <Input
                id={inputId}
                type='file'
                className='hidden'
                accept='.pdf,.doc,.docx,.jpg,.jpeg,.png,.gif'
                onChange={async e => {
                  const file = e.target.files?.[0];
                  if (file) {
                    await onUploadAttachment(file);
                    e.target.value = '';
                  }
                }}
              />
              <label htmlFor={inputId}>
                <Button
                  variant='ghost'
                  size='sm'
                  className='h-6 px-2 text-xs cursor-pointer'
                  disabled={isUploadingAttachment}
                  asChild
                >
                  <span>
                    {isUploadingAttachment ? (
                      <Loader2 className='h-3 w-3 animate-spin' />
                    ) : (
                      <Upload className='h-3 w-3' />
                    )}
                  </span>
                </Button>
              </label>
            </>
          )}
        </div>
        {attachments.length > 0 ? (
          <div className='space-y-1'>
            {attachments.map(att => (
              <WoundAttachmentRow
                key={att.id}
                attachment={att}
                onDownload={onDownloadAttachment}
                onDelete={onDeleteAttachment}
              />
            ))}
          </div>
        ) : (
          <p className='text-[11px] text-muted-foreground italic'>
            No attachments
          </p>
        )}
      </div>
    </div>
  );
};

export default WoundAssessmentCard;
